import type { NoeudAPlacer } from '../hierarchie';

/*
 * **Où se pose chaque galet de la Hiérarchie couchée** (chantier telephone-couche, 03 ; maquette
 * `app-au-telephone.html`, `hierarchie()`). L'arbre et son ordre sont ceux de la Hiérarchie
 * (`arbreVisible`, l'ordre du serveur) : ici, seulement les mesures de la maquette. De gauche à
 * droite, la racine, puis une colonne par niveau ; chaque tâche prend une ligne de 44 px, une
 * branche se centre sur ses lignes, la racine sur toutes. Sans rendu, testé dans
 * `tests/client/projets/placesDeLaHierarchieCouchee.test.ts`.
 */

/** Les mesures de la maquette : `.hn.racine` 160 × 52, `.hn.br` 190 × 44, `.hn.tc2` 210 × 36. */
export const RACINE = { largeur: 160, hauteur: 52 } as const;
export const BRANCHE = { largeur: 190, hauteur: 44 } as const;
export const TACHE = { largeur: 210, hauteur: 36 } as const;
/** Une ligne par tâche, le haut de la première, le bord gauche de la racine. */
export const LIGNE = 44;
export const HAUT = 14;
export const GAUCHE = 14;
/** Le bord gauche des branches (196), puis l'écart d'une colonne à la suivante (430 − 196). */
export const COLONNE_DES_BRANCHES = 196;
export const ECART_DES_COLONNES = 234;
/** La largeur du dessin (`.hier-in`, 660 px), qui s'allonge d'une colonne par niveau en plus. */
export const LARGEUR_DU_DESSIN = 660;
/** La courbe part et arrive à plat sur 20 px. */
const TANGENTE = 20;

export type SorteDeGalet = 'racine' | 'branche' | 'tache';

export interface Galet {
  id: string;
  sorte: SorteDeGalet;
  x: number;
  y: number;
}

/** Une courbe de la mère à sa bulle ; `vers` : la sorte du galet d'arrivée (son trait en dépend). */
export interface Courbe {
  de: string;
  a: string;
  vers: SorteDeGalet;
  d: string;
}

export interface PlacesDeLaHierarchie {
  galets: Galet[];
  courbes: Courbe[];
  largeur: number;
  hauteur: number;
}

const mesure = (sorte: SorteDeGalet) => (sorte === 'racine' ? RACINE : sorte === 'branche' ? BRANCHE : TACHE);
const gaucheDuNiveau = (niveau: number) => (niveau === 0 ? GAUCHE : COLONNE_DES_BRANCHES + (niveau - 1) * ECART_DES_COLONNES);

/**
 * Les places de l'arbre. `estBranche` : une bulle qui contient quelque chose (même si son dessous
 * n'est pas connu) se dessine en branche, une bulle au bout en tâche.
 */
export function placesDeLaHierarchieCouchee(
  arbre: NoeudAPlacer,
  estBranche: (id: string) => boolean
): PlacesDeLaHierarchie {
  const galets: Galet[] = [];
  const courbes: Courbe[] = [];
  let ligne = HAUT;
  let largeur = 0;

  /** Pose le galet et son dessous ; rend le centre de son bord droit. */
  function poser(noeud: NoeudAPlacer, niveau: number): { droite: number; milieu: number; sorte: SorteDeGalet } {
    const sorte: SorteDeGalet = niveau === 0 ? 'racine' : noeud.enfants.length > 0 || estBranche(noeud.id) ? 'branche' : 'tache';
    const { largeur: l, hauteur: h } = mesure(sorte);
    const x = gaucheDuNiveau(niveau);
    let y: number;
    if (noeud.enfants.length === 0) {
      // Une ligne à lui : la tâche s'y pose en haut, une branche sans dessous connu aussi.
      y = ligne;
      ligne += LIGNE;
      galets.push({ id: noeud.id, sorte, x, y });
    } else {
      const debut = ligne;
      const index = galets.push({ id: noeud.id, sorte, x, y: 0 }) - 1;
      const dessous = noeud.enfants.map((e) => ({ id: e.id, ...poser(e, niveau + 1) }));
      y = debut + (ligne - debut) / 2 - h / 2;
      galets[index] = { id: noeud.id, sorte, x, y };
      for (const e of dessous) {
        const xa = gaucheDuNiveau(niveau + 1);
        const x1 = x + l;
        const y1 = y + h / 2;
        courbes.push({
          de: noeud.id,
          a: e.id,
          vers: e.sorte,
          d: `M${x1} ${y1} C ${x1 + TANGENTE} ${y1}, ${xa - TANGENTE} ${e.milieu}, ${xa} ${e.milieu}`,
        });
      }
    }
    largeur = Math.max(largeur, x + l);
    return { droite: x + l, milieu: y + h / 2, sorte };
  }

  poser(arbre, 0);
  // 10 px sous la dernière ligne ; la racine seule, sous sa propre hauteur.
  const hauteur = Math.max(ligne, ...galets.map((g) => g.y + mesure(g.sorte).hauteur)) + 10;
  return { galets, courbes, largeur: Math.max(LARGEUR_DU_DESSIN, largeur + 20), hauteur };
}
